/**
 * Locked to the product OHT reference illustration (top view over the rail).
 * Design units scale into the layout footprint.
 *
 * The dark carriage band is centred on the FAB (red) track; the FOUP hood sits
 * behind it and the two gripper pods hang below.
 */
export const OHT_2D_DESIGN = {
  width: 148,
  height: 118,

  stroke: 1.75,

  /** FOUP hood behind the carriage — its lower edge is hidden by the band. */
  foup: { x: 18, y: 0, w: 112, h: 42, r: 10 },

  /** Dark carriage band straddling the rail. */
  band: { x: 4, y: 30, w: 140, h: 38, r: 10 },
  bandSheen: { x: 14, y: 33, w: 120, h: 3, r: 1.5 },

  lightL: { x: 30, y: 46.5, w: 18, h: 5, r: 2.5 },
  lightR: { x: 100, y: 46.5, w: 18, h: 5, r: 2.5 },

  /** Gripper pods under the carriage — tops tuck behind the band. */
  podL: { x: 14, y: 60, w: 58, h: 54, r: 9 },
  podR: { x: 76, y: 60, w: 58, h: 54, r: 9 },

  /** Vehicle name on the left pod. */
  nameText: { y: 90, fontSize: 14 },

  /** Brand mark on the right pod: four dots above the wordmark. */
  logo: { cx: 105, cy: 79, dot: 5, spread: 3.6 },
  logoText: { y: 90, fontSize: 14 },

  selectionPad: 2,
} as const;

export type Oht2DDesign = typeof OHT_2D_DESIGN;

const BAND_MID_Y = OHT_2D_DESIGN.band.y + OHT_2D_DESIGN.band.h / 2;

function normalizeDeg(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/**
 * Whether the artwork must turn 180° to stay right way up. The carriage band is
 * centred on the pivot, so the half-turn keeps it on the rail.
 */
export function isOht2DFlipped(headingDeg: number): boolean {
  const a = normalizeDeg(headingDeg);
  return a > 90 && a <= 270;
}

/**
 * On-screen rotation for an OHT with the given travel heading: along the rail,
 * never upside down. Horizontal rails draw at 0°, vertical rails at 90°.
 */
export function uprightOht2DRotation(headingDeg: number): number {
  const a = normalizeDeg(headingDeg);
  if (a > 90 && a <= 270) return a - 180;
  return a > 270 ? a - 360 : a;
}

/**
 * Local attach point = footprint centre = carriage band centre on the red track.
 * Layout + lane-snap place the OHT bbox centre on the FAB rail.
 */
export function getOht2DAttachLocal(
  width: number,
  _height: number,
  _showFoup = true
): { x: number; y: number } {
  return { x: width / 2, y: _height / 2 };
}

/** Scale + origin so the carriage band centre lands on (width/2, height/2). */
export function getOht2DLayout(
  width: number,
  height: number,
  showFoup = true
): { s: number; ox: number; oy: number } {
  const designW = OHT_2D_DESIGN.width;
  // Draw smaller than the footprint so OHTs stay compact on the FAB map.
  const s = (width * 0.72) / designW;
  const ox = (width - designW * s) / 2;
  const oy = height / 2 - BAND_MID_Y * s;
  void showFoup;
  return { s, ox, oy };
}
